import {
  _2dMapCombinedQuote,
  _2dMapQuote,
  _2dMapQuoteTypes,
  CustomOrderQuoteItem,
} from '@larki/shared/type/order.v2';
import classNames from 'classnames';
import { Col, Row } from 'react-bootstrap';
import text, { formatCurrencyForQuote } from '@/text';
import Card from './Card';
import WhiteContainer from '../../../components/containers/WhiteContainer';
import upperCase from 'lodash/upperCase';
import {
  customOrderKey,
  useEditablePrice,
  useOptionalStaffPriceEditContext,
} from '@/mp/components/Cart/StaffPriceEditContext';
import EditablePriceField from '@/mp/components/Cart/StaffPriceEditor/EditablePriceField';

const CustomOrderRow = ({
  order,
  quote,
  isApproximate,
}: {
  order: CustomOrderQuoteItem;
  quote: SectionProps['quote'];
  isApproximate: boolean;
}) => {
  const productKey = customOrderKey(order.id);
  const edit = useEditablePrice(productKey, order.price);

  return (
    <div
      key={order.id}
      className='d-flex flex-row justify-content-between align-items-center'
    >
      <p className='mb-0'>{order.name}</p>
      {edit.isEditing ? (
        <EditablePriceField
          fieldKey={`rfq-edit-${productKey}`}
          value={edit.editableValue}
          originalValue={edit.originalValue}
          scaleFactor={quote.currency.scale_factor}
          onChange={edit.setOverride}
          hasActiveOverride={edit.hasActiveOverride}
          product={productKey}
        />
      ) : (
        <p className='mb-0 justify-content-end'>
          {formatCurrencyForQuote(
            order.price,
            quote.currency.name,
            quote.currency.scale_factor,
            { spaced: true, approximate: isApproximate }
          )}
        </p>
      )}
    </div>
  );
};

export type CardType = {
  title: string;
  subQuote: _2dMapQuoteTypes['subQuote'];
};

interface SectionProps {
  cards: (CardType & { index: number })[];
  totals: _2dMapCombinedQuote['nonRfq']['totals'];
  quote:
    | _2dMapCombinedQuote
    | (_2dMapQuote & { nonRfq?: _2dMapQuote; rfq?: _2dMapQuote });
  isEssentialUser: boolean;
  title: string;
  totalsPrefix: string;
  isApproximate?: boolean;
  customOrders?: CustomOrderQuoteItem[];
}

function getQuoteForCard(quote: SectionProps['quote']): _2dMapQuote {
  // If nonRfq exists, use it (from _2dMapCombinedQuote),
  // otherwise use the quote itself
  if ('nonRfq' in quote && quote.nonRfq) {
    return quote.nonRfq;
  }
  if ('composition' in quote) {
    return quote;
  }

  throw new Error(
    'Invalid quote structure: missing both nonRfq and composition'
  );
}

const Section: React.FC<SectionProps> = ({
  cards,
  totals,
  quote,
  isEssentialUser,
  title,
  totalsPrefix,
  isApproximate = false,
  customOrders,
}) => {
  if (!cards) return null;

  const hasDiscounts = totals.discounts.length > 0;
  const quoteForCard = getQuoteForCard(quote);
  const fullPriceTotalLabel = `${upperCase(`${totalsPrefix} ${text('fullPriceTotal')}`)} ${text('excGst')}`;
  const discountsLabel = `${upperCase(`${totalsPrefix} ${text('discounts')}`)}`;
  const subtotalLabel = `${upperCase(`${totalsPrefix} ${text('subtotal')}`)}`;
  const gstLabel = `${upperCase(`${totalsPrefix} ${text('gst')}`)}`;
  const totalLabel = `${upperCase(`${totalsPrefix} ${text('total')}`)}`;
  const isTbc = totals.baseBeforeDiscounts === 'TBC';
  const editCtx = useOptionalStaffPriceEditContext();
  const isEditing = !!editCtx?.isEditing;

  return (
    <>
      <p className='mb-0 font-weight--400 font-16'>{title}</p>
      <Row>
        {cards.map((card, i) => {
          const shouldRemovePaddingRight = i % 2 === 0;
          return (
            <Col
              lg={12}
              xl={6}
              className={classNames('quote-card mb-3', {
                'pr-xl-0': shouldRemovePaddingRight,
              })}
              key={card.index}
            >
              <Card
                title={card.title}
                subQuote={card.subQuote}
                quote={quoteForCard}
                index={card.index}
                isApproximate={isApproximate}
              />
            </Col>
          );
        })}
        {customOrders && customOrders.length > 0 && (
          <Col
            lg={12}
            xl={6}
            className={classNames('quote-card mb-3', {
              'pr-xl-0': cards.length % 2 === 0,
              'mt-2': cards.length % 2 === 1,
            })}
          >
            <WhiteContainer className={classNames('p-3 charge-item', {
              'WhiteContainer--editing': isEditing,
            })}>
              <p className='title mb-2'>
                {cards.length > 0
                  ? cards[cards.length - 1].index + 2
                  : cards.length + 1}
                . Custom Orders
              </p>
              <div className='px-3'>
                {customOrders.map((order) => (
                  <CustomOrderRow
                    key={order.id}
                    order={order}
                    quote={quote}
                    isApproximate={isApproximate}
                  />
                ))}
              </div>
            </WhiteContainer>
          </Col>
        )}
      </Row>

      <Row>
        <Col lg={12} xl={6} className='pr-xl-0'>
          <div style={{ padding: '0 32px' }}>
            {hasDiscounts && (
              <>
                <div className='d-flex flex-row font-italic justify-content-between text-decoration-line-through font-grey--3'>
                  <p className='mb-0'>{fullPriceTotalLabel}</p>
                  <p className='mb-0'>
                    {formatCurrencyForQuote(
                      totals.baseBeforeDiscounts,
                      quote.currency.name,
                      quote.currency.scale_factor,
                      { spaced: true, approximate: isApproximate }
                    )}
                  </p>
                </div>

                <hr className='totals-divider mb-2 mt-3' />

                {totals.discounts.map((discount) => (
                  <div
                    className={classNames(
                      'd-flex flex-row font-italic justify-content-between pl-2 my-2 font-weight--400',
                      {
                        'font-pink': isEssentialUser,
                        'font-orange': !isEssentialUser,
                      }
                    )}
                  >
                    <p className='mb-0'>{discount.metadata.displayName}</p>
                    <p className='mb-0'>
                      {isTbc
                        ? text('toBeConfirmed')
                        : `—${formatCurrencyForQuote(
                            discount.amount,
                            quote.currency.name,
                            quote.currency.scale_factor,
                            { spaced: true }
                          )}`}
                    </p>
                  </div>
                ))}

                <hr className='totals-divider mt-2 mb-1' />

                <div
                  className={classNames(
                    'd-flex flex-row justify-content-between mb-3 font-weight--700 font-italic',
                    {
                      'font-pink': isEssentialUser,
                      'font-orange': !isEssentialUser,
                    }
                  )}
                >
                  <p className='mb-0'>{discountsLabel}</p>
                  <p className='mb-0'>
                    {isTbc
                      ? text('toBeConfirmed')
                      : `—${formatCurrencyForQuote(
                          totals.discountsTotal,
                          quote.currency.name,
                          quote.currency.scale_factor,
                          { spaced: true }
                        )}`}
                  </p>
                </div>
              </>
            )}

            <div className='d-flex flex-row justify-content-between my-1 font-weight--700'>
              <p className='mb-0'>{subtotalLabel}</p>
              <p className='mb-0'>
                {formatCurrencyForQuote(
                  totals.totalGross,
                  quote.currency.name,
                  quote.currency.scale_factor,
                  { spaced: true, approximate: isApproximate }
                )}
              </p>
            </div>
            <div className='d-flex flex-row justify-content-between my-1 font-weight--700'>
              <p className='mb-0'>{gstLabel}</p>
              <p className='mb-0'>
                {formatCurrencyForQuote(
                  totals.taxesTotal,
                  quote.currency.name,
                  quote.currency.scale_factor,
                  { spaced: true, approximate: isApproximate }
                )}
              </p>
            </div>

            <hr className='totals-divider my-1' />
            <div className='d-flex flex-row justify-content-between font-weight--700 font-green--1'>
              <p className='mb-0'>{totalLabel}</p>
              {formatCurrencyForQuote(
                totals.totalNet,
                quote.currency.name,
                quote.currency.scale_factor,
                { spaced: true, approximate: isApproximate }
              )}
            </div>
            <hr className='totals-divider my-1' />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default Section;
